export const FOCUSABLE_SELECTOR = [
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

/**
 * Applied to an element for exactly one programmatic focus() call, so its
 * focus ring can be suppressed in CSS (see base.css) — a real Tab press
 * always shows a ring, but a script-triggered focus() (e.g. autofocus on
 * modal/pane open) is not something the user visibly did themselves, so a
 * ring appearing out of nowhere reads as a stray "something is selected"
 * rather than a response to input. The class is removed the moment the
 * element blurs, so a genuine subsequent Tab press shows a ring normally.
 */
export const SILENT_INITIAL_FOCUS_CLASS = 'js-silent-initial-focus'

export function focusFirstElement(container: HTMLElement | null | undefined) {
  const focusable = container?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
  if (!focusable) {
    return
  }

  focusable.classList.add(SILENT_INITIAL_FOCUS_CLASS)
  focusable.addEventListener(
    'blur',
    () => focusable.classList.remove(SILENT_INITIAL_FOCUS_CLASS),
    { once: true },
  )
  focusable.focus()
}

/**
 * Keeps Tab/Shift+Tab cycling within `container` — call from a keydown
 * handler on 'Tab'. If focus is currently outside the container entirely,
 * it is pulled back in rather than left to escape further.
 */
export function trapTabFocus(e: KeyboardEvent, container: HTMLElement | null | undefined) {
  if (!container) {
    return
  }

  const focusableElements = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  )
  if (!focusableElements.length) {
    return
  }

  const first = focusableElements[0]
  const last = focusableElements[focusableElements.length - 1]
  const isOutsideContainer = !container.contains(document.activeElement)

  if (e.shiftKey) {
    if (isOutsideContainer || document.activeElement === first) {
      e.preventDefault()
      last?.focus()
    }
  } else if (isOutsideContainer || document.activeElement === last) {
    e.preventDefault()
    first?.focus()
  }
}

/**
 * Locates the modal/dialog's title element (marked with `data-modal-title`,
 * e.g. by BaseModal) and wires it to `container` via aria-labelledby. The
 * title is typically rendered arbitrarily deep inside `container`'s content,
 * so this is the only way to associate it without threading an id prop
 * through every individual modal component.
 */
export function wireDialogLabel(container: HTMLElement | null | undefined) {
  if (!container) {
    return
  }

  const titleEl = container.querySelector<HTMLElement>('[data-modal-title]')
  if (!titleEl) {
    container.removeAttribute('aria-labelledby')
    return
  }

  if (!titleEl.id) {
    titleEl.id = `modal-title-${Math.random().toString(36).slice(2)}`
  }
  container.setAttribute('aria-labelledby', titleEl.id)
}
